import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Container,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { Idea, IdeaStatus, ideasApi } from '../services/api';

const STATUS: { value: IdeaStatus; label: string; color: 'primary' | 'warning' | 'success' | 'default' }[] = [
  { value: 'neu', label: 'Neu', color: 'primary' },
  { value: 'vorgemerkt', label: 'Vorgemerkt', color: 'warning' },
  { value: 'erledigt', label: 'Erledigt', color: 'success' },
  { value: 'verworfen', label: 'Verworfen', color: 'default' },
];

const GAME_LABEL: Record<string, string> = { quiz: 'Rattenfänger-Quiz', rangiert: 'Rangiert' };
const gameLabel = (g: string) => GAME_LABEL[g] || g;
const fmtDate = (d: string | null) =>
  d ? new Date(d).toLocaleString('de-DE', { dateStyle: 'short', timeStyle: 'short' }) : '–';

// Die Texte stammen von Spielern. React zeigt sie als reinen Text an (kein HTML).
const AdminIdeen: React.FC = () => {
  const [ideas, setIdeas] = useState<Idea[] | null>(null);
  const [error, setError] = useState('');
  const [filter, setFilter] = useState<IdeaStatus | 'alle'>('neu');
  const [copied, setCopied] = useState(false);

  const load = useCallback(async () => {
    try {
      setIdeas(await ideasApi.list());
      setError('');
    } catch (err: any) {
      setError(err.response?.data?.error || 'Ideen konnten nicht geladen werden');
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const counts = useMemo(() => {
    const c: Record<string, number> = { alle: ideas?.length ?? 0 };
    for (const i of ideas ?? []) c[i.status] = (c[i.status] || 0) + 1;
    return c;
  }, [ideas]);

  const visible = useMemo(
    () => (ideas ?? []).filter((i) => filter === 'alle' || i.status === filter),
    [ideas, filter]
  );

  const replace = (updated: Idea) =>
    setIdeas((list) => (list ?? []).map((i) => (i._id === updated._id ? updated : i)));

  const change = async (idea: Idea, patch: { status?: IdeaStatus; note?: string }) => {
    try {
      replace(await ideasApi.update(idea._id, patch));
    } catch (err: any) {
      setError(err.response?.data?.error || 'Speichern fehlgeschlagen');
    }
  };

  const remove = async (idea: Idea) => {
    if (!window.confirm('Diese Idee endgültig löschen?')) return;
    try {
      await ideasApi.remove(idea._id);
      setIdeas((list) => (list ?? []).filter((i) => i._id !== idea._id));
    } catch (err: any) {
      setError(err.response?.data?.error || 'Löschen fehlgeschlagen');
    }
  };

  // Die aktuell gezeigte Liste als Text, z. B. um sie in eine Anfrage an Claude zu kopieren.
  const copyList = async () => {
    const md = visible
      .map((i, n) => `${n + 1}. [${gameLabel(i.game)}] ${i.text.replace(/\n+/g, ' ')} (${i.name || 'anonym'}, ${fmtDate(i.createdAt)})`)
      .join('\n');
    try {
      await navigator.clipboard.writeText(md);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      setError('Kopieren nicht möglich (Browser blockiert die Zwischenablage)');
    }
  };

  return (
    <Container maxWidth="md">
      <Typography variant="h5" component="h1" sx={{ fontWeight: 600, mb: 1 }}>
        Ideen aus den Partyspielen
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Das reichen Spieler am Handy ein (Lobby und Siegerehrung). Hier liegen sie nur zum Ansehen – es passiert
        nichts automatisch. Was du umsetzen willst, setzt du auf „Vorgemerkt“ oder gibst es weiter.
      </Typography>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

      <Stack direction="row" spacing={1} sx={{ mb: 2, flexWrap: 'wrap', rowGap: 1 }} alignItems="center">
        {[{ value: 'alle', label: 'Alle' }, ...STATUS].map((s) => (
          <Chip
            key={s.value}
            label={`${s.label} (${counts[s.value] || 0})`}
            color={filter === s.value ? 'primary' : 'default'}
            variant={filter === s.value ? 'filled' : 'outlined'}
            onClick={() => setFilter(s.value as IdeaStatus | 'alle')}
          />
        ))}
        <Box sx={{ flexGrow: 1 }} />
        <Button size="small" onClick={copyList} disabled={visible.length === 0}>
          {copied ? 'Kopiert ✓' : 'Liste kopieren'}
        </Button>
        <Button size="small" onClick={load}>Aktualisieren</Button>
      </Stack>

      {!ideas && !error && <Box sx={{ textAlign: 'center', py: 4 }}><CircularProgress /></Box>}

      {ideas && visible.length === 0 && (
        <Paper sx={{ p: 3, textAlign: 'center' }}>
          <Typography color="text.secondary">Keine Ideen in dieser Ansicht.</Typography>
        </Paper>
      )}

      <Stack spacing={2}>
        {visible.map((idea) => (
          <IdeaCard key={idea._id} idea={idea} onChange={change} onRemove={remove} />
        ))}
      </Stack>
    </Container>
  );
};

const IdeaCard: React.FC<{
  idea: Idea;
  onChange: (idea: Idea, patch: { status?: IdeaStatus; note?: string }) => void;
  onRemove: (idea: Idea) => void;
}> = ({ idea, onChange, onRemove }) => {
  const [note, setNote] = useState(idea.note);
  useEffect(() => setNote(idea.note), [idea.note]);

  return (
    <Paper sx={{ p: 2 }}>
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1, flexWrap: 'wrap', rowGap: 0.5 }}>
        <Chip size="small" label={gameLabel(idea.game)} />
        <Typography variant="caption" color="text.secondary">
          {idea.name || 'anonym'}{idea.room ? ` · Raum ${idea.room}` : ''} · {fmtDate(idea.createdAt)}
        </Typography>
      </Stack>
      <Typography sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', mb: 2 }}>{idea.text}</Typography>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'center' }}>
        <Select
          size="small"
          value={idea.status}
          onChange={(e) => onChange(idea, { status: e.target.value as IdeaStatus })}
          sx={{ minWidth: 150 }}
        >
          {STATUS.map((s) => <MenuItem key={s.value} value={s.value}>{s.label}</MenuItem>)}
        </Select>
        <TextField
          size="small"
          fullWidth
          placeholder="Notiz (nur für dich)"
          value={note}
          inputProps={{ maxLength: 1000 }}
          onChange={(e) => setNote(e.target.value)}
          onBlur={() => note !== idea.note && onChange(idea, { note })}
        />
        <Button size="small" color="error" onClick={() => onRemove(idea)}>Löschen</Button>
      </Stack>
    </Paper>
  );
};

export default AdminIdeen;
